import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Pause, Play, AlertTriangle, CheckCircle, Clock, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { useAuth } from "../../contexts/AuthContext";
import { getRollout, pauseRollout, resumeRollout, getTelemetry } from "../../lib/api";
import { useToast } from "../../contexts/ToastContext";
import { formatDate, rollbackRatePercent } from "../../lib/utils";

const PHASE_STATE_ICON = {
  COMPLETE: <CheckCircle className="h-5 w-5 text-emerald-500" />,
  IN_PROGRESS: <Clock className="h-5 w-5 text-blue-500 animate-pulse" />,
  PAUSED: <AlertTriangle className="h-5 w-5 text-amber-500" />,
  PENDING: <div className="h-5 w-5 rounded-full border-2 border-slate-200" />,
};

// A paused rollout always reads as a warning, regardless of its stored state.
function stateBadgeVariant(isPaused, state) {
  if (isPaused) return "warning";
  if (state === "COMPLETE") return "success";
  return "info";
}

// Timeline values are tinted only when the caller marks them warn or success.
function timelineValueClass(warn, success) {
  if (warn) return "text-amber-700 font-medium";
  if (success) return "text-emerald-700 font-medium";
  return "text-slate-900";
}

export default function RolloutDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canAccess } = useAuth();
  const { toast } = useToast();

  const [rollout, setRollout] = useState(null);
  const [telemetry, setTelemetry] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(false);

  useEffect(() => {
    Promise.all([getRollout(id), getTelemetry({ rolloutId: id })]).then(([r, t]) => {
      setRollout(r);
      setTelemetry(t);
      setLoading(false);
    });
  }, [id]);

  const handlePause = async () => {
    setActioning(true);
    try {
      await pauseRollout(id);
      setRollout((prev) => ({ ...prev, state: "PAUSED", pausedAt: new Date().toISOString() }));
      toast({ title: "Rollout paused", description: "Rollout suspended.", variant: "warning" });
    } catch {
      toast({ title: "Failed to pause", variant: "destructive" });
    } finally {
      setActioning(false);
    }
  };

  const handleResume = async () => {
    setActioning(true);
    try {
      await resumeRollout(id);
      setRollout((prev) => ({ ...prev, state: "CANARY", pausedAt: null }));
      toast({ title: "Rollout resumed", description: "Rollout resumed.", variant: "success" });
    } catch {
      toast({ title: "Failed to resume", variant: "destructive" });
    } finally {
      setActioning(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-400">Loading…</div>;
  if (!rollout) return <div className="p-8 text-center text-slate-400">Rollout not found.</div>;

  const deployed = rollout.successCount + rollout.rollbackCount;
  const overallPct = rollout.targetCount > 0 ? Math.round((deployed / rollout.targetCount) * 100) : 0;
  const rateHigh = rollout.rollbackRate > rollout.rollbackRateThreshold;
  const isActive = ["CANARY", "PHASE_25", "PHASE_100"].includes(rollout.state);
  const isPaused = rollout.state === "PAUSED";
  const canPause = canAccess("FleetAdmin", "ReleaseManager", "SupportEngineer") && isActive;
  const canResume = canAccess("FleetAdmin", "ReleaseManager") && isPaused;

  return (
    <div className="max-w-4xl space-y-5">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/rollouts")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="flex items-center gap-3 flex-1">
          <h2 className="text-lg font-semibold">Rollout — v{rollout.baselineVersion}</h2>
          <Badge variant={stateBadgeVariant(isPaused, rollout.state)}>
            {rollout.state}
          </Badge>
        </div>
        {canPause && (
          <Button variant="outline" size="sm" onClick={handlePause} disabled={actioning}>
            {actioning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Pause className="mr-2 h-4 w-4" />}
            Pause Rollout
          </Button>
        )}
        {canResume && (
          <Button size="sm" onClick={handleResume} disabled={actioning}>
            {actioning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
            Resume Rollout
          </Button>
        )}
      </div>

      {rateHigh && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Rollback rate {rollbackRatePercent(rollout.rollbackRate)}</strong> exceeds threshold of{" "}
            {rollbackRatePercent(rollout.rollbackRateThreshold)}. This rollout has been withdrawn automatically.
          </AlertDescription>
        </Alert>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Target", value: rollout.targetCount.toLocaleString(), color: "text-slate-900" },
          { label: "Success", value: rollout.successCount.toLocaleString(), color: "text-emerald-700" },
          { label: "Rollbacks", value: rollout.rollbackCount, color: "text-red-600" },
          { label: "Rollback Rate", value: rollbackRatePercent(rollout.rollbackRate), color: rateHigh ? "text-red-600" : "text-slate-900" },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="pt-5 pb-4">
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Overall progress */}
      <Card>
        <CardHeader><CardTitle className="text-base">Overall Progress</CardTitle></CardHeader>
        <CardContent>
          <Progress
            value={overallPct}
            className="h-3"
            indicatorClassName={rateHigh ? "bg-amber-500" : "bg-blue-600"}
          />
          <p className="text-sm text-slate-500 mt-2">
            {deployed.toLocaleString()} of {rollout.targetCount.toLocaleString()} devices deployed ({overallPct}%) ·{" "}
            {rollout.pendingCount.toLocaleString()} pending
          </p>
        </CardContent>
      </Card>

      {/* Phase diagram */}
      <Card>
        <CardHeader><CardTitle className="text-base">Rollout Tiers</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-4">
            {rollout.phases.map((phase, i) => {
              const total = phase.successCount + phase.rollbackCount;
              const target = Math.round((phase.targetPct / 100) * rollout.targetCount);
              const phasePct = target > 0 ? Math.min(100, Math.round((total / target) * 100)) : 0;
              return (
                <div key={i} /* NOSONAR S6479: raw backend rows carry no guaranteed unique id; a composite key risks duplicates. */ className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    {PHASE_STATE_ICON[phase.state] || PHASE_STATE_ICON.PENDING}
                    {i < rollout.phases.length - 1 && (
                      <div className={`w-0.5 h-8 mt-1 ${phase.state === "COMPLETE" ? "bg-emerald-300" : "bg-slate-100"}`} />
                    )}
                  </div>
                  <div className="flex-1 pb-2">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium">{phase.label}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span className="text-emerald-600 font-medium">✓ {phase.successCount}</span>
                        <span className="text-red-500 font-medium">↩ {phase.rollbackCount}</span>
                      </div>
                    </div>
                    {phase.state !== "PENDING" && (
                      <Progress
                        value={phasePct}
                        className="h-1.5"
                        indicatorClassName={phase.state === "COMPLETE" ? "bg-emerald-500" : "bg-blue-600"}
                      />
                    )}
                    {phase.state === "PENDING" && (
                      <div className="h-1.5 bg-slate-100 rounded-full" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Telemetry for this rollout */}
      <Card>
        <CardHeader><CardTitle className="text-base">Telemetry Events</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {telemetry.length === 0 && (
              <p className="text-center text-slate-400 py-6 text-sm">No telemetry yet.</p>
            )}
            {telemetry.map((t) => (
              <div key={t.id} className="flex items-center gap-3 px-4 py-3">
                {t.outcome === "SUCCESS"
                  ? <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
                  : <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
                }
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{t.deviceSerial}</p>
                  {t.errorCode && <p className="text-xs text-red-600">{t.errorCode}</p>}
                </div>
                <Badge variant={t.outcome === "SUCCESS" ? "success" : "destructive"} className="text-[11px]">
                  {t.outcome}
                </Badge>
                <span className="text-xs text-slate-400 shrink-0">{formatDate(t.reportedAt)}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card>
        <CardHeader><CardTitle className="text-base">Timeline</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <TimelineRow label="Started" value={formatDate(rollout.startedAt)} />
            {rollout.pausedAt && <TimelineRow label="Paused" value={formatDate(rollout.pausedAt)} warn />}
            {rollout.completedAt && <TimelineRow label="Completed" value={formatDate(rollout.completedAt)} success />}
            <TimelineRow label="Last updated" value={formatDate(rollout.updatedAt)} />
            <TimelineRow label="Segment" value={rollout.targetSegmentName} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function TimelineRow({ label, value, warn, success }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-500">{label}</span>
      <span className={timelineValueClass(warn, success)}>
        {value}
      </span>
    </div>
  );
}
